import { Button, DatePicker, Dropdown, Menu, Modal, Pagination, Select, Space, TableColumnsType, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";

import { ContentWrapper } from "../../Components/Layout/ContentWrapper";
import { DataTable } from "../../Components/DataTable/DataTable";
import { getColoriezedValue } from "../../Utils/getColorizedValue";
import { getFormattedDate, getKey } from "../../Utils/CommonUtils";
import { columnWidth } from "../../Components/DataTable/Properties";
import { panelUrl, toArray, workOrderApi } from "../../Services";

import { approachLabel, workOrderStatus, workOrderStatusLabel } from "./Constants";
import { WorkOrderFilter, WorkOrderFilters, filterParams } from "./WorkOrderFilter";
import styles from "./WorkOrder.module.scss";
import vars from "../../Styles/variables.module.scss";

const { RangePicker } = DatePicker;

/** A work order as its row reads — shared by the table and its Export. */
const toRow = (item: any) => ({
  ...item,
  // The API calls the UI's "Cost Centre" a division.
  costCentre: item.division ?? item.costCentre,
  workCategory: item.category ?? item.workCategory,
  commencementDate: getFormattedDate(item.commencementDate) || "-",
  completionDate: getFormattedDate(item.completionDate) || "-",
  requestedDate: getFormattedDate(item.requestedDate) || "-",
  createdDate: getFormattedDate(item.createdOn) || "-",
});

/**
 * Work Order — list screen.
 *
 * Follows the same shape as the other panel list pages (ManageCatalog /
 * PanelUserManagement): ContentWrapper for the card + title + CTAs, DataTable
 * for search/export/selection, an inline RangePicker + Status select for the
 * filters, and antd Pagination underneath driven by server-side `totalRecords`.
 *
 * Filters/paging are mirrored into the URL via useSearchParams and restored on
 * mount, matching the convention used across the SPA list screens.
 *
 * Rows come from GET /work-order/list (V3), which accepts the paging, search,
 * date, status and org filters this screen exposes.
 */
export const WorkOrder = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsObj = Object.fromEntries(searchParams.entries());
  /* Search runs on the server: /work-order/list takes `searchText`, and with
     one page of ten rows in hand a client-side filter could only ever look at
     what is already on screen — a work order on page 4 was unfindable.
     `searchText` was already read off the URL and passed to the endpoint, but
     nothing ever wrote it, so it was permanently empty. */
  const [clearSearchInput, setClearSearchInput] = useState(false);
  /* The debounced callback is built once, so it must not capture a stale copy
     of the URL params. */
  const paramsRef = useRef(paramsObj);
  paramsRef.current = paramsObj;

  const runSearch = useMemo(
    () =>
      debounce((value: string) => {
        const next: Record<string, string> = { ...paramsRef.current, workOrderPage: "0" };
        if (value) next.searchText = value;
        else delete next.searchText;
        setSearchParams(next);
      }, 500),
    [setSearchParams]
  );

  const pageParam = Number(searchParams.get("workOrderPage"));
  const page = !isNaN(pageParam) ? pageParam : 0;
  const searchText = searchParams.get("searchText") ?? "";
  const status = searchParams.get("status") ?? "";
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");

  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState(false);
  /* The list request, re-run page by page by DataTable's own Export — the
     Manage Catalog pattern, so "All" is every page, not the ten on screen. */
  const [exportUrl, setExportUrl] = useState("");
  /**
   * Newest first.
   *
   * It defaulted to commencement date, which bears no relation to when
   * anything happened. `updatedOn` was the obvious replacement and was wrong:
   * a work order just saved has no update stamp, so sorting on it pushed the
   * newest drafts to the BOTTOM — off page one entirely, which reads as "the
   * draft was saved but does not appear in the list". `createdOn` is always
   * set, and is the endpoint's own default. */
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderByName, setOrderByName] = useState("createdOn");
  const [pickerKey, setPickerKey] = useState(0);
  const dateRangeValue: any =
    startDate && endDate ? [moment(startDate), moment(endDate)] : null;
  /* Multi-select filters round-trip through the URL as comma-separated
     lists, so a filtered view stays linkable and survives refresh. */
  const readList = (key: string): string[] => {
    const raw = searchParams.get(key);
    return raw ? raw.split(",").filter(Boolean) : [];
  };

  const activeFilters: WorkOrderFilters = {
    procurementApproach: readList("procurementApproach"),
    category: readList("category"),
    subsidiary: readList("subsidiary"),
    costCentre: readList("costCentre"),
  };

  const applyFilters = (filters: WorkOrderFilters) => {
    const next: Record<string, string> = { ...paramsObj, workOrderPage: "0" };
    (Object.keys(filters) as (keyof WorkOrderFilters)[]).forEach((key) => {
      if (filters[key].length) {
        next[key] = filters[key].join(",");
      } else {
        delete next[key];
      }
    });
    setSearchParams(next);
  };

  const getRecords = (currentPage: number = page) => {
    setIsLoading(true);
    const params = {
      page: currentPage,
      size: recordsPerPage,
      searchText: searchText || undefined,
      order: sortOrder,
      orderBy: orderByName,
      status: status || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      ...filterParams(activeFilters),
    };
    setExportUrl(panelUrl("/work-order/list", params));
    workOrderApi
      .list(params)
      .then((res: any) => {
        setData(
          toArray(res).map((item: any, index: number) => ({
            ...toRow(item),
            key: getKey(currentPage, index),
          }))
        );
        setTotalRecords(res?.totalCount ?? 0);
      })
      .catch(() => {
        setData([]);
        setTotalRecords(0);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getRecords(page);
    // eslint-disable-next-line
  }, [
    page,
    searchText,
    status,
    startDate,
    endDate,
    sortOrder,
    orderByName,
    activeFilters.procurementApproach.join(","),
    activeFilters.category.join(","),
    activeFilters.subsidiary.join(","),
    activeFilters.costCentre.join(","),
  ]);

  const handleTableSort = (column: any, order: string) => {
    setOrderByName(column.dataIndex);
    setSortOrder(order === "asc" ? "desc" : "asc");
  };

  /* Clearing the picker drops both dates from the URL. They used to be
     overwritten only when set, so the old range stayed in the URL and the list
     stayed filtered — empty — after the dates were cleared. */
  const setDate = (dates: any) => {
    const { startDate: _start, endDate: _end, ...rest } = paramsObj;
    setSearchParams({
      ...rest,
      ...(dates?.startDate && { startDate: dates.startDate }),
      ...(dates?.endDate && { endDate: dates.endDate }),
      workOrderPage: "0",
    });
  };

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  /* The box clears the moment they type; only the request waits for a pause. */
  const handleSearchChange = (value: string) => {
    setClearSearchInput(false);
    runSearch(value);
  };

  const clearSearchData = () => {
    runSearch.cancel();
    setClearSearchInput(true);
    setSearchParams({ workOrderPage: "0" });
  };


  /* View is the only row action: editing happens from the details page, per
     section, and work orders are not deleted from the list. */
  const menuClick = (key: string, record: any) => {
    if (key === "view") navigate(`/workorder/view/${encodeURIComponent(record.envelopeId ?? record.workOrderNo)}`);
    /* A draft reopens in the wizard, as the detail page's Edit does. */
    if (key === "edit") navigate(`/workorder/add?envelopeId=${encodeURIComponent(record.envelopeId)}`);
  };

  const columns: TableColumnsType = [
    { title: "Work Order No.", dataIndex: "workOrderNo", sorter: true, width: columnWidth.GENERAL },
    { title: "Job Title", dataIndex: "jobTitle", sorter: true, width: columnWidth.PROJECT_NAME },
    { title: "Subsidiary/Division", dataIndex: "subsidiary", sorter: true, width: columnWidth.DEPARTMENT_NAME },
    { title: "Cost Centre", dataIndex: "costCentre", sorter: true, width: columnWidth.DESIGNATION },
    { title: "Work Category", dataIndex: "workCategory", sorter: true, width: columnWidth.DEPARTMENT_NAME },
    { title: "Region", dataIndex: "region", sorter: true, width: columnWidth.DESIGNATION },
    {
      title: "Type of Approach",
      dataIndex: "procurementApproach",
      sorter: true,
      width: columnWidth.DESIGNATION,
      render: (value: string) => approachLabel(value) || "-",
    },
    { title: "Vendor name", dataIndex: "vendorName", sorter: true, width: columnWidth.DESIGNATION },
    {
      title: "Status",
      dataIndex: "status",
      sorter: true,
      width: columnWidth.GENERAL,
      /* The API's code (PENDING_REVIEW) as people read it ("Created"). */
      render: (value: string) => getColoriezedValue(workOrderStatusLabel(value).toLowerCase()),
    },
    { title: "Commencement Date", dataIndex: "commencementDate", sorter: true, width: columnWidth.DATE },
    { title: "Completion Date", dataIndex: "completionDate", sorter: true, width: columnWidth.DATE },
    { title: "Requester Name", dataIndex: "requesterName", sorter: true, width: columnWidth.DESIGNATION },
    { title: "Requester Email", dataIndex: "requesterEmail", sorter: true, width: columnWidth.EMAIL },
    { title: "Requested date", dataIndex: "requestedDate", sorter: true, width: columnWidth.DATE },
    { title: "Created Date", dataIndex: "createdDate", sorter: true, width: columnWidth.DATE },
    {
      title: "Action",
      dataIndex: "action",
      /* Wide enough that the header does not wrap to "Actio / n". */
      width: 90,
      fixed: "right",
      render: (_value: any, record: any) => (
        <Dropdown
          overlay={
            <Menu
              /* The menu renders in a portal, but a React portal still bubbles
                 its events up the REACT tree — through the cell this Dropdown
                 sits in, whose onCell handler navigates to the detail page.
                 Both fired and the row navigation ran last, so picking Edit
                 landed on View. */
              onClick={({ key, domEvent }) => {
                domEvent.stopPropagation();
                menuClick(key, record);
              }}
              items={[
                {
                  key: "view",
                  label: (
                    <Typography.Text style={{ color: vars?.primary }}>
                      <Space>
                        <Icon
                          icon="akar-icons:link-out"
                          inline
                          fontSize={16}
                        />
                        View
                      </Space>
                    </Typography.Text>
                  ),
                },
                ...(String(record.status ?? "").trim().toUpperCase() === "DRAFT"
                  ? [
                      {
                        key: "edit",
                        label: (
                          <Typography.Text style={{ color: vars?.primary }}>
                            <Space>
                              <Icon
                                icon="akar-icons:edit"
                                inline
                                fontSize={16}
                              />
                              Edit
                            </Space>
                          </Typography.Text>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          }
          trigger={["click"]}
          placement="bottomRight"
        >
          <span onClick={(e) => e.stopPropagation()}>
            <Icon
              icon="bi:three-dots-vertical"
              style={{ cursor: "pointer" }}
            />
          </span>
        </Dropdown>
      ),
    },
  ];


  return (
    <ContentWrapper
      title="Work Order"
      actionItems={
        <Space>
          <Button
            type="primary"
            onClick={() => navigate("/workorder/add")}
          >
            <Space>
              <Icon
                icon="akar-icons:plus"
                inline
              />
              Add
            </Space>
          </Button>
        </Space>
      }
    >
      <DataTable
        columns={columns.map((col: any) => ({
          ...col,
          onHeaderCell: (column: any) => ({
            onClick: () => {
              if (column.sorter) {
                handleTableSort(column, sortOrder);
              }
            },
          }),
        }))}
        className="table-clickable"
        tableData={data}
        isLoading={isLoading}
        exportFileName="Work Order"
        hideExport={false}
        exportApiUrl={exportUrl}
        exportRowMapper={(record: any) => ({
          ...toRow(record),
          status: workOrderStatusLabel(record.status),
          procurementApproach: approachLabel(record.procurementApproach),
        })}
        excludeExportCols={["Action"]}
        onClick={(record) => navigate(`/workorder/view/${encodeURIComponent(record.envelopeId ?? record.workOrderNo)}`)}
        additionalCTA={
          <Space
            wrap
            style={{ width: "100%", justifyContent: "flex-end" }}
          >
            <RangePicker
              /* A fresh picker per opening: rc-picker remembers the month you
                 browsed to and has no prop to steer it, so it reopened on e.g.
                 Oct 2015. New, it starts on today — or on the chosen range. */
              key={pickerKey}
              onOpenChange={(open) => !open && setPickerKey((key) => key + 1)}
              value={dateRangeValue}
              format="DD MMM YYYY"
              placeholder={["From Date", "To Date"]}
              onChange={(dates: any) =>
                setDate({
                  startDate: dates?.[0] ? dates[0].format("YYYY-MM-DD") : "",
                  endDate: dates?.[1] ? dates[1].format("YYYY-MM-DD") : "",
                })
              }
            />
            <Select
              className="w-100"
              style={{ minWidth: 160 }}
              placeholder="Select Status"
              value={status || undefined}
              allowClear={!!status}
              getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
              onChange={(value: string) =>
                setSearchParams({ ...paramsObj, status: value ?? "", workOrderPage: "0" })
              }
            >
              {workOrderStatus.map((option) => (
                <Select.Option
                  value={option.value}
                  key={option.value}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            <WorkOrderFilter
              value={activeFilters}
              onApply={applyFilters}
            />
          </Space>
        }
        totalRecords={totalRecords}
        inputDisable={isLoading}
        isPagination={true}
        onSearchChange={handleSearchChange}
        isServerSearchApi
        clearStringSearch={clearSearchInput}
        searchPlaceholderText="Search by Work order no./ Job title"
        recordsPerPage={recordsPerPage}
      />

      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={page + 1}
          total={totalRecords}
          onChange={(nextPage) =>
            setSearchParams({ ...paramsObj, workOrderPage: String(nextPage - 1) })
          }
          showSizeChanger={false}
        />
      </div>
    </ContentWrapper>
  );
};

